/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
// External imports
import { tracked } from '@glimmer/tracking';
import moment from 'moment';
import { scaleOrdinal } from 'd3-scale';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ThresholdLevel from 'linced-app/enums/threshold-level';
import ResponseActivity from '@linc-technologies/linced-common/enums/response-activity-group';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';

// Ember imports
import Controller from '@ember/controller';
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { intersect, alias } from '@ember/object/computed';
import {
  byKeys,
  compact,
  filterBy,
  findBy,
  mapBy,
  sortBy,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';
import {
  getStructuresFromDates,
  isPeriodPresentOnDay,
} from 'linced-app/utils/timetables';

function labelForTruancyCode(code) {
  switch (code) {
    case 'P':
      return 'Present';
    case 'U':
      return 'Unjustified';
    case 'J':
      return 'Justified';
    case 'O':
      return 'Overseas Posting';
  }
  return 'Unknown';
}

export default class AttendanceController extends Controller {
  // Services
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  session;

  @service
  dates;

  @service
  fetch;

  @service
  strings;

  @service
  terms;

  // detailDialog variables
  @tracked
  detailDialogError = '';

  @tracked
  detailDialogOpen = false;
  @tracked
  detailDialogSubtitle = '';
  @tracked
  detailDialogTitle = '';
  @tracked
  filteredLearnersForDetailDialog = '';
  ThresholdLevel = ThresholdLevel;
  ResponseActivity = ResponseActivity;

  // Table variables
  @tracked numDaysAttendanceToDisplay = 14;

  @tracked
  selected = [];
  @tracked selectedAmp = [];

  // filteredSelectedPeople filters out people not visible in the table
  // from the selected people
  @intersect('filteredAmpDataLearners', 'selectedAmpLearners')
  filteredSelectedPeople;

  @tracked filters = [];
  @tracked sortDir = 'asc';
  @tracked sortProp = 'fullName';

  @alias('session.school.jurisdiction')
  jurisdiction;

  @alias('terms.currentTerm')
  currentTerm;

  @computed('currentTerm')
  get previousTerm() {
    let currentTerm = this.currentTerm;
    if (!currentTerm || !currentTerm.previous) {
      return null;
    }

    return {
      start: this.get('currentTerm.previous.start'),
      end: this.get('currentTerm.previous.end'),
    };
  }

  @computed('model.currentTermThresholds.[]')
  get currTermThresholdsMap() {
    return this.model.currentTermThresholds.reduce((map, threshold) => {
      if (!map[threshold.person_id]) {
        map[threshold.person_id] = [];
      }
      map[threshold.person_id].push(threshold);
      return map;
    }, {});
  }

  @computed('model.previousTermThresholds.[]')
  get prevTermThresholdsMap() {
    return this.model.previousTermThresholds.reduce((map, threshold) => {
      if (!map[threshold.person_id]) {
        map[threshold.person_id] = [];
      }
      map[threshold.person_id].push(threshold);
      return map;
    }, {});
  }

  @alias('session.school')
  school;

  // Should track movements if in NSW
  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  // Timetable period label (periods/blocks)
  @computed('strings.timetablePeriods', 'trackMovements')
  get typeLabel() {
    if (this.trackMovements) {
      return '(days)';
    }
    return `(${this.strings.timetablePeriods})`;
  }

  // Attendance Records
  @computed('model.records.[]')
  get allRecords() {
    // Skip any records with no code or from non-current group members.
    return this.model.records.filter((record) => {
      if (isEmpty(record.code)) {
        return false;
      }
      return this.model.group.currentLearnerMemberIds.includes(
        record.person_id,
      );
    });
  }

  @computed('model.dayCodes.[]')
  get allDayCodes() {
    // Skip any records with no code or from non-current group members.
    return this.model.dayCodes.filter((record) => {
      if (isEmpty(record.code)) {
        return false;
      }
      return this.model.group.currentLearnerMemberIds.includes(
        record.person_id,
      );
    });
  }

  @computed(
    'allRecords.[]',
    'terms.currentAcademicYearTerms.[]',
    'trackMovements',
  )
  get byRawCode() {
    let data = [];
    let allRecords = this.allRecords;
    let yearToDate = {};
    let rawData = {};
    let scheme = this.getColourScheme();

    // For each term this year, build up data to be charted.
    this.terms.currentAcademicYearTerms.forEach((term) => {
      // Filter down to just the records for the term in question
      let records = allRecords.filter((record) => {
        return record.date >= term.start && record.date <= term.end;
      });

      // Build up map of code -> count
      if (this.trackMovements) {
        rawData = records.reduce((data, record) => {
          record.movements.forEach((movement) => {
            let code = movement.code;
            data[code] = data[code] ? data[code] + 1 : 1;
            yearToDate[code] = yearToDate[code] ? yearToDate[code] + 1 : 1;
          });
          return data;
        }, {});
      } else {
        rawData = records.reduce((data, record) => {
          let code = record.code;
          data[code] = data[code] ? data[code] + 1 : 1;
          yearToDate[code] = yearToDate[code] ? yearToDate[code] + 1 : 1;
          return data;
        }, {});
      }

      data.push(
        this.mapData(rawData, records.length, 'raw', scheme, term.term),
      );
    });

    data.push(
      this.mapData(yearToDate, allRecords.length, 'raw', scheme, 'ytd'),
    );

    return data;
  }

  @computed(
    'allRecords.[]',
    'allDayCodes.[]',
    'terms.currentAcademicYearTerms.[]',
    'trackMovements',
  )
  get byTruancyCode() {
    let data = [];
    let allRecords = this.allRecords;
    let yearToDate = {};
    let rawData = {};
    let ytdRecordlength = 0;
    let scheme = this.getColourScheme();

    // For each term this year, build up data to be charted.
    this.terms.currentAcademicYearTerms.forEach((term) => {
      if (this.trackMovements) {
        // Initialise dayCodes for current term
        let dayCodes = this.allDayCodes.filter((dayCode) => {
          return dayCode.date >= term.start && dayCode.date <= term.end;
        });

        rawData = dayCodes.reduce((data, dayCode) => {
          let updateData = (key, value) => {
            data[key] = (data[key] || 0) + value;
            yearToDate[key] = (yearToDate[key] || 0) + value;
          };

          if (dayCode.present > 0) {
            updateData('P', dayCode.presentWholeDays);
          }
          if (dayCode.unjustifiablyAbsent > 0) {
            updateData('U', dayCode.unjustifiablyAbsentWholeDays);
          }
          if (dayCode.justifiedAbsent > 0) {
            updateData('J', dayCode.justifiedAbsentWholeDays);
          }
          return data;
        }, {});
      } else {
        // Initialise records for current term
        let records = allRecords.filter((record) => {
          return record.date >= term.start && record.date <= term.end;
        });

        rawData = records.reduce((data, record) => {
          let code = this.attendanceCodes.truancyMap[record.code];
          data[code] = data[code] ? data[code] + 1 : 1;
          yearToDate[code] = yearToDate[code] ? yearToDate[code] + 1 : 1;
          return data;
        }, {});
      }

      let termTotal = Object.values(rawData).reduce(
        (sum, value) => sum + value,
        0,
      );
      data.push(this.mapData(rawData, termTotal, 'truancy', scheme, term.term));
      ytdRecordlength += termTotal;
    });

    data.push(
      this.mapData(yearToDate, ytdRecordlength, 'truancy', scheme, 'ytd'),
    );

    return data;
  }

  // Table export data
  @computed('sortedLearners.[]')
  get exportData() {
    return this.sortedLearners.map((learner) => {
      let row = [
        learner.firstName,
        learner.lastName,
        learner.gender,
        learner.currentYearLevel,
        learner.presentPercent,
      ];
      if (!learner.attendanceRecords) {
        return row;
      }
      learner.attendanceRecords.forEach((record) => {
        if (record) {
          row.push(record.code);
        } else {
          row.push('');
        }
      });

      return row;
    });
  }

  @computed('model.group.name')
  get exportTitle() {
    return `${this.model.group.name} Attendance`;
  }

  @computed('filteredAmpData.[]')
  get ampExportData() {
    return this.filteredAmpData.map((learner) => {
      let row = [
        learner.fullName,
        learner.currentYearLevel,
        learner.previousTermThreshold ? learner.previousTermThreshold : '',
        learner.currentTermThreshold ? learner.currentTermThreshold : '',
        learner.absentCodeSummary,
      ];
      if (learner.dateCrossed) {
        row.push(moment(learner.dateCrossed).format('DD/MM/YYYY'));
      } else {
        row.push('');
      }

      this.terms.currentAcademicYearTerms.forEach((term) => {
        let absences = learner.learnerAbsences[term.term];
        if (!isEmpty(absences)) {
          row.push(absences);
        } else {
          row.push('-');
        }
      });

      let ytdAbsences = learner.ytdAbsences;
      if (!isEmpty(ytdAbsences)) {
        row.push(ytdAbsences);
      } else {
        row.push('-');
      }

      if (learner.completedDate) {
        row.push(moment(learner.completedDate).format('DD/MM/YYYY'));
      } else {
        row.push('');
      }

      if (learner.responseActivity) {
        row.push(learner.responseActivity);
      } else {
        row.push('');
      }

      return row;
    });
  }

  @computed('numDaysAttendanceToDisplay')
  get weekDaysToDisplay() {
    let date = moment();
    let structures = this.dates.structures;
    let weekdaysToDisplay = [1, 2, 3, 4, 5];
    let structuresForDates = getStructuresFromDates(
      date
        .subtract(this.numDaysAttendanceToDisplay, 'day')
        .format('YYYY-MM-DD'),
      date.format('YYYY-MM-DD'),
      structures,
    );
    if (isPeriodPresentOnDay(structuresForDates, 6)) {
      weekdaysToDisplay.push(6);
    }
    if (isPeriodPresentOnDay(structuresForDates, 7)) {
      weekdaysToDisplay.push(0);
    }
    return weekdaysToDisplay;
  }

  get recentDays() {
    let recentDays = [];

    let date = moment();

    for (let i = 0; i < this.numDaysAttendanceToDisplay; i++) {
      let weekday = date.weekday();
      if (this.weekDaysToDisplay.includes(weekday)) {
        recentDays.push({
          displayDate: date.format('DD/MM'),
          date: date.format('YYYY-MM-DD'),
          isMonday: weekday === 1,
        });
      }
      date = date.subtract(1, 'day');
    }

    return recentDays;
  }

  @computed(
    'allRecords.[]',
    'allDayCodes.[]',
    'numDaysAttendanceToDisplay',
    'trackMovements',
  )
  get recentRecordsByLearner() {
    let startDate = moment()
      .subtract(this.numDaysAttendanceToDisplay, 'days')
      .format('YYYY-MM-DD');
    if (this.trackMovements) {
      return this.allDayCodes.reduce((recentRecordsByLearner, record) => {
        if (record.date <= startDate) {
          return recentRecordsByLearner;
        }
        if (record.person_id in recentRecordsByLearner) {
          recentRecordsByLearner[record.person_id].push(record);
        } else {
          recentRecordsByLearner[record.person_id] = new Array(record);
        }
        return recentRecordsByLearner;
      }, {});
    } else {
      return this.allRecords.reduce((recentRecordsByLearner, record) => {
        if (record.date <= startDate) {
          return recentRecordsByLearner;
        }

        if (record.person_id in recentRecordsByLearner) {
          recentRecordsByLearner[record.person_id].push(record);
        } else {
          recentRecordsByLearner[record.person_id] = new Array(record);
        }
        return recentRecordsByLearner;
      }, {});
    }
  }

  @computed('allRecords.[]')
  get attendanceRecordsByLearner() {
    return this.allRecords.reduce((recentRecordsByLearner, record) => {
      // We only want to display absent code summary for the current term
      if (record.date < this.currentTerm.start) {
        return recentRecordsByLearner;
      }
      if (record.person_id in recentRecordsByLearner) {
        recentRecordsByLearner[record.person_id].push(record);
      } else {
        recentRecordsByLearner[record.person_id] = new Array(record);
      }
      return recentRecordsByLearner;
    }, {});
  }

  @computed(
    'model.learners.[]',
    'currTermThresholdsMap.[]',
    'prevTermThresholdsMap.[]',
    'absentRecordsByLearner.[]',
    'learnerAbsencesByTerm',
    'terms.currentAcademicYearTerms.[]',
    'sortProp',
    'sortDir',
  )
  get learnerAmpData() {
    return this.get('model.learners').map((learner) => {
      let lastResponseActivity = this.getLastResponseActivity(learner.id);
      let learnerAbsences = this.learnerAbsencesByTerm[learner.id] || {};

      return {
        id: learner.id,
        fullName: learner.firstName + ' ' + learner.lastName,
        gender: learner.gender,
        currentYearLevel: learner.currentYearLevel,
        dateCrossed: this.getDateCrossed(
          this.currTermThresholdsMap[learner.id],
        ),
        currentTermThreshold: this.getCurrentThreshold(
          this.currTermThresholdsMap[learner.id],
        ),
        previousTermThreshold: this.getPreviousThreshold(
          this.prevTermThresholdsMap[learner.id],
        ),
        absentCodeSummary: this.generateAbsentCodeSummary(
          this.attendanceRecordsByLearner[learner.id],
        ),
        completedDate: lastResponseActivity
          ? moment(lastResponseActivity.completedDate, 'YYYY-MM-DD').toDate()
          : null,
        responseActivity: lastResponseActivity
          ? ResponseActivity.getLabel(lastResponseActivity.responseActivity)
          : null,
        learnerAbsences,
        ytdAbsences: learnerAbsences.ytd,
      };
    });
  }

  @computed('sortProp', 'sortDir', 'learnerAmpData')
  get sortedAmpData() {
    let sorted = this.learnerAmpData;

    // Sorting by a Person property.
    sorted = sortBy(sorted, this.sortProp);

    // Reverse the array if descending sort was requested.
    if (this.sortDir === 'desc') {
      sorted = sorted.reverse();
    }
    return sorted;
  }

  // filteredData applies the user's filters to the data array computed above.
  @computed('sortedAmpData.[]', 'filters.@each.{field,value}')
  get filteredAmpData() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.sortedAmpData;
    }

    // Map raw filters into what we need to actually perform the filtering.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;

      if (key.startsWith('learnerAbsences.') || key === 'ytdAbsences') {
        dataType = DataType.INTEGER;
      }

      return {
        key,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return this.sortedAmpData.filter((row) => {
      return filters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  getDateCrossed(thresholds) {
    if (isEmpty(thresholds)) {
      return null;
    }

    let sortedThresholds = thresholds
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .reverse();
    let prevThreshold = null;
    let dateCrossed = null;

    for (let threshold of sortedThresholds) {
      if (isEmpty(prevThreshold)) {
        prevThreshold = threshold;
        dateCrossed = threshold.date;
        continue;
      }
      if (threshold.threshold !== prevThreshold.threshold) {
        dateCrossed = prevThreshold.date;
        return moment(dateCrossed, 'YYYY-MM-DD').toDate();
      }
      prevThreshold = threshold;
      dateCrossed = threshold.date;
    }

    return moment(dateCrossed, 'YYYY-MM-DD').toDate();
  }

  getCurrentThreshold(thresholds) {
    if (isEmpty(thresholds)) {
      return null;
    }

    // Grab the latest threshold
    let threshold = thresholds
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .at(-1);

    return threshold ? ThresholdLevel.getLabel(threshold.threshold) : null;
  }

  getPreviousThreshold(thresholds) {
    if (isEmpty(thresholds)) {
      return null;
    }

    let previousTermThreshold = thresholds.find(
      (item) => item.date === this.previousTerm.end,
    );

    return previousTermThreshold
      ? ThresholdLevel.getLabel(previousTermThreshold.threshold)
      : null;
  }

  generateAbsentCodeSummary(records) {
    if (isEmpty(records)) {
      return '';
    }
    let summaryMap = {};
    let absentCodes = [];

    records.forEach((record) => {
      if (
        this.attendanceCodes.truancyUCodes.some(
          (code) => code.code === record.code,
        ) ||
        this.attendanceCodes.truancyJCodes.some(
          (code) => code.code === record.code,
        )
      ) {
        absentCodes.push(record.code);
      }
    });

    absentCodes.forEach((code) => {
      if (!summaryMap[code]) {
        summaryMap[code] = 0;
      }
      summaryMap[code]++;
    });

    let entries = Object.entries(summaryMap).map(([code, count]) => {
      let percent = Math.floor((count / absentCodes.length) * 100);
      return `${code} - ${percent}%`;
    });

    return entries.join(' | ');
  }

  getLastResponseActivity(personId) {
    let activities = this.model.responseActivities.filter(
      (activity) => activity.person_id === personId,
    );
    return activities
      .slice()
      .sort((a, b) => a.createTime - b.createTime)
      .at(-1);
  }

  // Attendance data for table
  @computed('model.learners.[]', 'recentRecordsByLearner')
  get learnerAttendance() {
    return this.get('model.learners')
      .filter((learner) => !isEmpty(this.recentRecordsByLearner[learner.id]))
      .map((learner) => {
        // We need to grab attendance data for each day
        // we want to show a summary for in the table (2 weeks)
        let twoWeeksAttendanceRecords = this.recentDays.map((date) => {
          return filterBy(
            this.recentRecordsByLearner[learner.id],
            'date',
            date.date,
          )
            .sort(byKeys('period.endTime'))
            .at(-1);
        });

        // Return data for this row
        return {
          id: learner.id,
          firstName: learner.firstName,
          lastName: learner.lastName,
          gender: learner.gender,
          currentYearLevel: learner.currentYearLevel,
          presentPercent: this.getPresentPercentage(
            compact(twoWeeksAttendanceRecords),
          ),
          attendanceRecords: twoWeeksAttendanceRecords,
        };
      });
  }

  @computed('sortProp', 'sortDir', 'learnerAttendance')
  get sortedLearners() {
    let sorted = this.learnerAttendance;

    // Sorting by a Person property.
    sorted = sorted.sort(byKeys(this.sortProp));

    // Reverse the array if descending sort was requested.
    if (this.sortDir === 'desc') {
      sorted = sorted.reverse();
    }
    return sorted;
  }

  @computed('model.learners.[]')
  get learnersById() {
    return this.get('model.learners').reduce((map, learner) => {
      map[learner.id] = learner;
      return map;
    }, {});
  }

  @computed('selected.[]', 'learnersById')
  get selectedLearners() {
    return this.selected.map(
      (learnerData) => this.learnersById[learnerData.id],
    );
  }

  @computed('selectedAmp.[]', 'learnersById')
  get selectedAmpLearners() {
    return this.selectedAmp.map(
      (learnerData) => this.learnersById[learnerData.id],
    );
  }

  @computed('filteredAmpData.[]', 'learnersById')
  get filteredAmpDataLearners() {
    return this.filteredAmpData.map(
      (learnerData) => this.learnersById[learnerData.id],
    );
  }

  @computed('model.{learners.[],termlyAbsences.[]}')
  get learnerAbsencesByTerm() {
    let learners = this.model.learners;
    // Only NZ_MOE schools with current-year terms have termly absences.
    let dayCodesByTerm = this.model.termlyAbsences ?? [];

    let map = {};

    learners.forEach((learner) => {
      map[learner.id] = {};
      map[learner.id]['ytd'] = 0;
    });

    dayCodesByTerm.forEach((termEntry) => {
      let term = termEntry.term;
      if (!term) {
        return;
      }

      let termDayCodes = termEntry.dayCodes || [];

      termDayCodes.forEach((dayCode) => {
        let learnerId = dayCode.person_id;
        if (!map[learnerId]) {
          return;
        }

        map[learnerId][term] = dayCode.absentWholeDays;
        map[learnerId]['ytd'] += dayCode.absentWholeDays;
      });
    });

    return map;
  }

  // Map raw data into something more usable for charts and tables
  mapData(rawData, total, type, scheme, termName) {
    let tooltipMeasure = this.strings.timetablePeriods;
    if (this.trackMovements) {
      if (type === 'truancy') {
        tooltipMeasure = 'days';
      }
      if (type === 'raw') {
        tooltipMeasure = 'marks';
      }
    }
    let data = [];
    let codes = Object.keys(rawData);
    let displayName = termName === 'ytd' ? 'YTD' : 'Term ' + termName;
    let noData = 'No Data';
    if (codes.length === 0) {
      data.push({
        value: noData,
        label: noData,
        count: 1,
        tooltip: noData,
        color: '#808080',
        termName: termName,
      });
      return { termName: termName, displayName: displayName, data: data };
    }
    codes.forEach((code) => {
      let label = this.getLabel(code, type);
      let percentage = '';
      let count = rawData[code];
      if (count > 0 && total > 0) {
        percentage = ((count / total) * 100).toFixed(1) + '%';
      }

      // Handle pluralization for 'days'
      let displayMeasure = tooltipMeasure;
      if (tooltipMeasure === 'days' && count === 1) {
        displayMeasure = 'day';
      }

      data.push({
        value: code,
        label,
        count,
        tooltip: `${label} (${code}): ${percentage} (${count} ${displayMeasure})`,
        color: this.getColour(code, scheme),
        termName: termName,
      });
    });

    return { termName: termName, displayName: displayName, data: data };
  }

  // Detail Dialog helpers
  // Opens the detail dialog based on the element of the chart clicked.
  openDetailDialog(termName, code, recordFilter) {
    let timeBounds = this.getTimeBounds(termName);

    if (!timeBounds) {
      return;
    }

    let filteredRecordsByTimeAndCode = recordFilter(
      code,
      timeBounds.from,
      timeBounds.to,
    );

    this.filteredLearnersForDetailDialog =
      this.getLearnersFromAttendanceRecords(filteredRecordsByTimeAndCode);

    this.detailDialogOpen = true;
  }

  // Gets the from and to dates for terms and ytd.
  getTimeBounds(termName) {
    let isYtd = termName === 'ytd';

    if (isYtd) {
      let yearBounds = this.dates.getYearBounds(this.dates.currentAcademicYear);

      return { from: yearBounds.from, to: yearBounds.to };
    }

    let termNum = parseInt(termName);
    let term = findBy(this.terms.currentAcademicYearTerms, 'term', termNum);

    if (!term) {
      this.detailDialogError = 'An error occurred: No term found!';
      return;
    }

    return { from: term.start, to: term.end };
  }

  // Filters attendance records based on attendance code,
  // used to filter the array of all records down to just the records
  // included in this part of the chart.
  filterRecordsByAttendanceCodeAndDate(code, from, to) {
    return this.allRecords.filter((record) => {
      return record.code === code && record.date >= from && record.date <= to;
    });
  }

  // Filters attendance records based on truancy code,
  // used to filter the array of all records down to just the records
  // included in this part of the chart.
  filterRecordsByTruancyCodeAndDate(code, from, to) {
    return this.allRecords.filter((record) => {
      return (
        this.attendanceCodes.truancyMap[record.code] === code &&
        record.date >= from &&
        record.date <= to
      );
    });
  }

  // Table Helpers
  // Matches personIds from attendance records and returns an array of person objects.
  // Potential for low performance here. If this is running slowly this could be a culprit.
  getLearnersFromAttendanceRecords(records) {
    return mapBy(uniqBy(records, 'person_id'), 'person');
  }

  // Gets the percentage of periods a learner was present for,
  // over the provided records according to NSW truancy codes
  getPresentPercentage(records) {
    let total = records.length;
    let present = 0;

    if (this.trackMovements) {
      ({ present, total } = records.reduce(
        (counts, dayCode) => {
          counts.present += dayCode.present;
          counts.total += 2;
          return counts;
        },
        { present: 0, total: 0 },
      ));
    } else {
      present = records.filter((record) => {
        return this.attendanceCodes.truancyMap[record.code] === 'P';
      }).length;
    }

    if (total === 0) {
      return 'No Data';
    }

    return Math.round((present / total) * 100);
  }

  // Helpers for colour and label
  getLabel(code, type) {
    switch (type) {
      case 'truancy':
        return labelForTruancyCode(code);
      case 'raw':
        if (code === 'X') {
          return 'Exam leave';
        }
        if (code === 'V') {
          return 'Unsupervised exam study';
        }
        return get(
          findBy(this.attendanceCodes.allCodes, 'code', code),
          'label',
        );
    }

    return 'Unknown';
  }

  getColour(code, scheme) {
    switch (code) {
      case 'P':
        return '#2ca02c';
      case 'J':
        return '#ff7f0e';
      case 'U':
        return '#d62728';
    }

    // Fall back to colour scheme.
    return scheme(code);
  }

  getColourScheme() {
    return scaleOrdinal([
      '#d62728',
      '#ff9896',
      '#9467bd',
      '#8c564b',
      '#e377c2',
      '#7f7f7f',
      '#bcbd22',
      '#17becf',
      '#aec7e8',
      '#ffbb78',
      '#98df8a',
      '#c5b0d5',
      '#c49c94',
      '#f7b6d2',
      '#c7c7c7',
      '#dbdb8d',
      '#9edae5',
    ]);
  }

  // These actions all use slightly different strings/functions and are
  // responsible for opening the detail dialog with the correct parameters.
  @action
  openTruancyCodeDetailDialog(data) {
    let termName = data.termName;
    let code = data.value;
    this.detailDialogTitle = `Truancy Code: ${code}`;
    this.detailDialogSubtitle = `Students with truancy code ${code} during ${
      termName === 'ytd' ? this.dates.currentAcademicYear : 'Term ' + termName
    }`;
    this.openDetailDialog(
      termName,
      code,
      this.filterRecordsByTruancyCodeAndDate.bind(this),
    );
  }

  @action
  openAttendanceCodeDetailDialog(data) {
    let termName = data.termName;
    let code = data.value;
    this.detailDialogTitle = `Attendance Code: ${code}`;
    this.detailDialogSubtitle = `Students with attendance code ${code} during ${
      termName === 'ytd' ? this.dates.currentAcademicYear : 'Term ' + termName
    }`;
    this.openDetailDialog(
      termName,
      code,
      this.filterRecordsByAttendanceCodeAndDate.bind(this),
    );
  }

  @action
  openMovementDetailDialog(data) {
    let termName = data.termName;
    let code = data.value;

    this.detailDialogTitle = `Movement Code: ${code}`;
    this.detailDialogSubtitle = `Students with movement code ${code} during ${
      termName === 'ytd' ? this.dates.currentAcademicYear : 'Term ' + termName
    }`;
    this.openDetailDialog(
      termName,
      code,
      this.filterRecordsByAttendanceCodeAndDate.bind(this),
    );
  }

  // Actions used for the attendance data table
  @action
  changeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  toggleAllSelections() {
    if (this.selected.length === this.sortedLearners.length) {
      // All already selected; clear selection.
      this.selected = [];
    } else {
      // Not all selected; select them all.
      let selected = this.selected;
      this.sortedLearners.forEach((learner) => {
        if (!selected.includes(learner)) {
          selected = [...selected, learner];
        }
      });
      this.selected = selected;
    }
  }

  @action
  toggleAllSelectionsAmp() {
    let allFilteredSelected = this.filteredAmpData.every((learnerData) =>
      this.selectedAmp.includes(learnerData),
    );
    if (allFilteredSelected) {
      let selectedAmp = this.selectedAmp;
      this.filteredAmpData.forEach((learnerData) => {
        selectedAmp = selectedAmp.filter((item) => item !== learnerData);
      });
      this.selectedAmp = selectedAmp;
    } else {
      let selectedAmp = this.selectedAmp;
      this.filteredAmpData.forEach((learnerData) => {
        if (!selectedAmp.includes(learnerData)) {
          selectedAmp = [...selectedAmp, learnerData];
        }
      });
      this.selectedAmp = selectedAmp;
    }
  }

  @action
  toggleSelected(person) {
    if (this.selected.includes(person)) {
      this.selected = this.selected.filter((item) => item !== person);
    } else {
      this.selected = [...this.selected, person];
    }
  }

  @action
  toggleSelectedAmp(person) {
    if (this.selectedAmp.includes(person)) {
      this.selectedAmp = this.selectedAmp.filter((item) => item !== person);
    } else {
      this.selectedAmp = [...this.selectedAmp, person];
    }
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((item) => item !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
